import { useQuery } from "@apollo/client";
import ContentCopy from "@mui/icons-material/ContentCopy";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Route from "@mui/icons-material/Route";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { ProgramArea } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenant } from "hooks/useTenant";
import React, {
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";
import { GET_REPORT } from "routes/Customer/Chemicals/Report/useReport";
import { useFacility } from "routes/Customer/Facility/useFacility";
import { GET_TIERII_REPORT } from "routes/Staff/Fulfillment/FulfillmentDetails/useReportDetails";
import { validate as isValidUuid } from "uuid";
import {
  GotoUuidCommand,
  type GotoUuidHandle,
} from "./commandPalette/GotoUuidCommand";
import { DefaultCommandRegistrations } from "./CommandPaletteDefaults";
import { Command, CommandRegistryContext } from "./CommandPaletteRegistry";

function keywordsEqual(a: string[] | undefined, b: string[] | undefined) {
  const x = a ?? [];
  const y = b ?? [];
  if (x.length !== y.length) return false;
  const sx = [...x].sort().join(",");
  const sy = [...y].sort().join(",");
  return sx === sy;
}

const CommandPaletteRow = memo(function CommandPaletteRow({
  cmd,
  idx,
  isSelected,
  onSelectCommand,
  setItemRef,
}: {
  cmd: Command;
  idx: number;
  isSelected: boolean;
  onSelectCommand: (cmd: Command) => void;
  setItemRef: (idx: number, el: HTMLDivElement | null) => void;
}) {
  let iconElement: React.ReactNode = null;
  if (cmd.icon) {
    iconElement = cmd.icon;
  } else if (cmd.type === "goto") iconElement = <Route fontSize="small" color="action" />;
  else if (cmd.type === "open") iconElement = <OpenInNew fontSize="small" color="action" />;
  else if (cmd.type === "copy" || cmd.type === "copy-link") iconElement = <ContentCopy fontSize="small" color="action" />;
  return (
    <ListItemButton
      ref={(el) => setItemRef(idx, el)}
      role="option"
      aria-selected={isSelected}
      selected={isSelected}
      onClick={() => onSelectCommand(cmd)}
    >
      {iconElement != null ? (
        <ListItemIcon sx={{ minWidth: 40 }}>
          {iconElement}
        </ListItemIcon>
      ) : null}
      <ListItemText
        primary={cmd.label}
        secondary={cmd.description}
        primaryTypographyProps={{ noWrap: true }}
        secondaryTypographyProps={{
          noWrap: true,
          ...(cmd.type === "copy-link" && {
            color: "primary",
            sx: { textDecoration: "underline" },
          }),
        }}
      />
    </ListItemButton>
  );
});

function useReportUrlContext() {
  const matchCustomerReport = useMatch(
    "/o/:tenantId/chemicals/reports/:reportId/*"
  );
  const matchStaffReport = useMatch("/staff/fulfillment/r/:reportId/*");

  const reportId = matchCustomerReport?.params?.reportId
    ? matchCustomerReport.params.reportId
    : matchStaffReport?.params?.reportId;

  const tenantIdFromCustomerReport = matchCustomerReport?.params?.tenantId;

  return {
    reportId,
    tenantIdFromCustomerReport,
    isStaffRoute: !!matchStaffReport,
    isCustomerRoute: !!matchCustomerReport,
  };
}

function useStaffImplementationContext() {
  const matchStaffImplementation = useMatch(
    "/staff/implementation/:tenantId/*"
  );
  return {
    tenantIdFromStaffImplementation: matchStaffImplementation?.params?.tenantId,
  };
}

function isEditableElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea") return true;
  if (target.isContentEditable) return true;
  return false;
}

function StaffCommandPalette({ children }: PropsWithChildren<unknown>) {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, tenant: currentUseTenant } = useTenant();
  const { facilityId: facilityIdParam } = useParams<{ facilityId?: string }>();
  const { data: facilityData } = useFacility(facilityIdParam);
  const {
    reportId,
    tenantIdFromCustomerReport,
    isStaffRoute,
    isCustomerRoute,
  } = useReportUrlContext();
  const { tenantIdFromStaffImplementation } = useStaffImplementationContext();

  const staffReportQuery = useQuery(GET_TIERII_REPORT, {
    variables: { reportId: reportId ?? "", version: undefined },
    skip: !isStaffRoute || !reportId,
  });

  const customerReportQuery = useQuery(GET_REPORT, {
    variables: {
      reportId: reportId ?? "",
      programArea: ProgramArea.Epcra,
      version: undefined,
    },
    skip: !isCustomerRoute || !reportId,
  });

  const reportFacility = isStaffRoute
    ? staffReportQuery.data?.tierIIReport?.facility
    : customerReportQuery.data?.tierIIReport?.facility;
  const reportTenantId = isStaffRoute
    ? staffReportQuery.data?.tierIIReport?.tenantId
    : customerReportQuery.data?.tierIIReport?.tenantId;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const gotoUuidRef = useRef<GotoUuidHandle | null>(null);
  const openGotoId = useCallback(
    (id: string) => gotoUuidRef.current?.openWithValue(id),
    []
  );

  // Commands registry state
  const [dynamicSets, setDynamicSets] = useState<Record<string, Command[]>>({});
  const registerCommands = useCallback((sourceKey: string, cmds: Command[]) => {
    setDynamicSets((prev) => ({ ...prev, [sourceKey]: cmds }));
    return () =>
      setDynamicSets((prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [sourceKey]: _removed, ...rest } = prev;
        return rest;
      });
  }, []);

  const replaceCommands = useCallback((sourceKey: string, cmds: Command[]) => {
    setDynamicSets((prev) => {
      const existing = prev[sourceKey];
      // Require equality by id + signature + label + description + type + keywords
      const same =
        Array.isArray(existing) &&
        existing.length === cmds.length &&
        existing.every((c, i) => {
          const next = cmds[i];
          return (
            c.id === next?.id &&
            c.signature === next?.signature &&
            c.label === next?.label &&
            (c.description ?? "") === (next?.description ?? "") &&
            (c.type ?? undefined) === (next?.type ?? undefined) &&
            keywordsEqual(c.keywords, next?.keywords)
          );
        });
      if (same) return prev;
      return { ...prev, [sourceKey]: cmds };
    });
  }, []);
  const mergedCommands = useMemo(() => {
    const all = Object.values(dynamicSets).flat();
    const byId = new Map<string, Command>();
    for (const c of all) byId.set(c.id, c);
    return Array.from(byId.values());
  }, [dynamicSets]);

  const effectiveTenantId =
    tenantIdFromCustomerReport ??
    tenantIdFromStaffImplementation ??
    reportTenantId ??
    tenantId;

  let tenant = currentUseTenant;
  if (effectiveTenantId && effectiveTenantId !== tenant?.id) {
    // If the effective tenant id is different from the current user tenant,
    // we need to ignore the tenant from useTenant()
    // For example if we're on the staff implementation page for a tenant,
    // we want to use the tenant from the URL path, not the current user tenant
    // (i.e. the tenant that appears in the top right of the page)
    tenant = undefined;
  }

  const derived = useMemo(() => {
    const encodedTenantId = effectiveTenantId
      ? encodeURIComponent(effectiveTenantId)
      : undefined;

    const facilityId = facilityIdParam ?? reportFacility?.id;
    const facilityNameRaw =
      facilityData?.facility?.name ?? reportFacility?.name ?? facilityId;
    const facilityAltId =
      facilityData?.facility?.customerFacilityId ??
      reportFacility?.customerFacilityId;
    const facilityDisplay = facilityAltId
      ? `${facilityNameRaw} (${facilityAltId})`
      : facilityNameRaw;
    const encodedFacilityId = facilityId
      ? encodeURIComponent(facilityId)
      : undefined;

    const encodedReportId = reportId ? encodeURIComponent(reportId) : undefined;

    return {
      tenantId: effectiveTenantId,
      encodedTenantId,
      facilityId,
      facilityDisplay,
      encodedFacilityId,
      reportId,
      encodedReportId,
    };
  }, [
    effectiveTenantId,
    facilityIdParam,
    facilityData?.facility?.name,
    facilityData?.facility?.customerFacilityId,
    reportFacility?.id,
    reportFacility?.name,
    reportFacility?.customerFacilityId,
    reportId,
  ]);

  const getAppBaseUrl = useCallback(() => {
    if (typeof window === "undefined") return "";
    return window.location.origin;
  }, []);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(textarea);
      }
    }
  }, []);

  const escapeHtml = (unsafe: string) =>
    unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const copyRichLink = useCallback(
    async (url: string, label: string) => {
      const html = `<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`;
      const sanitizeForSlack = (s: string) => s.replace(/[>|]/g, " ");
      const slackPlain = `<${url}|${sanitizeForSlack(label)}>`;

      try {
        if (navigator.clipboard && globalThis.ClipboardItem) {
          const item = new globalThis.ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([slackPlain], { type: "text/plain" }),
          });
          await navigator.clipboard.write([item]);
          return;
        }
      } catch {
        // fall through to text-only fallback
      }

      // Fallback: copy Slack-formatted plain text so Slack pastes a named link
      await copyToClipboard(slackPlain);
    },
    [copyToClipboard]
  );

  const filteredCommands = useMemo(() => {
    if (!query.trim()) return mergedCommands;
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    return mergedCommands.filter((c) => {
      const keywordPart = (c.keywords ?? []).join(" ");
      const haystack = `${c.label} ${
        c.description ?? ""
      } ${keywordPart}`.toLowerCase();
      return tokens.every((t) => haystack.includes(t));
    });
  }, [mergedCommands, query]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open, location.key]);

  const handleGlobalKeyDown = useCallback((event: KeyboardEvent) => {
    if (isEditableElement(event.target)) return;
    const isK = event.key.toLowerCase() === "k";
    const isMod = event.metaKey || event.ctrlKey;
    if (isK && isMod) {
      event.preventDefault();
      setOpen((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [handleGlobalKeyDown]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
      return;
    }
    if (!filteredCommands.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % filteredCommands.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(
        (i) => (i - 1 + filteredCommands.length) % filteredCommands.length
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = filteredCommands[activeIndex];
      if (cmd) {
        setOpen(false);
        setQuery("");
        Promise.resolve(cmd.run());
      }
    }
  };

  useEffect(() => {
    const el = itemRefs.current[activeIndex];
    if (el && listContainerRef.current) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, filteredCommands.length, open]);

  useEffect(() => {
    // Clamp active index when results list size changes
    setActiveIndex((i) =>
      filteredCommands.length === 0
        ? 0
        : Math.min(i, filteredCommands.length - 1)
    );
  }, [filteredCommands.length]);

  useEffect(() => {
    // Reset to first item on query change
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    // Keep refs array size in sync
    itemRefs.current = Array(filteredCommands.length).fill(null);
  }, [filteredCommands.length]);

  const handleSelectCommand = useCallback((command: Command) => {
    setOpen(false);
    setQuery("");
    Promise.resolve(command.run());
  }, []);

  const setItemRef = useCallback((idx: number, el: HTMLDivElement | null) => {
    itemRefs.current[idx] = el;
  }, []);

  const openPalette = useCallback(() => setOpen(true), []);

  const handleClosePalette = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  const contextValue = useMemo(
    () => ({
      commands: mergedCommands,
      registerCommands,
      replaceCommands,
      openPalette,
      openGotoId,
    }),
    [
      mergedCommands,
      registerCommands,
      replaceCommands,
      openPalette,
      openGotoId,
    ]
  );

  return (
    <CommandRegistryContext.Provider value={contextValue}>
      {children}
      <DefaultCommandRegistrations
        derived={derived}
        tenantLabel={tenant?.name || ""}
        navigate={(p) => navigate(p)}
        getAppBaseUrl={getAppBaseUrl}
        copyToClipboard={copyToClipboard}
        copyRichLink={copyRichLink}
      />
      <GotoUuidCommand ref={gotoUuidRef} />
      <Dialog
        open={open}
        onClose={handleClosePalette}
        fullWidth
        maxWidth="sm"
        noPrimaryAction
        contentSx={{ height: "70vh", display: "flex" }}
        DialogContentProps={{ onKeyDown: handleKeyDown }}
      >
        <TextField
          inputRef={inputRef}
          autoFocus
          fullWidth
          size="small"
          placeholder="Type a command..."
          value={query}
          onChange={(e) => {
            const value = e.target.value;
            // Detect pasted UUIDs and auto-switch to Goto UUID dialog
            if (isValidUuid(value.trim())) {
              setOpen(false);
              setQuery("");
              gotoUuidRef.current?.openWithValue(value.trim());
              return;
            }
            setQuery(value);
          }}
        />
        <Box
          ref={listContainerRef}
          sx={{ mt: 1, flexGrow: 1, minHeight: 0, overflowY: "auto" }}
        >
          {filteredCommands.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography color="text.secondary" variant="body2">
                No commands
              </Typography>
            </Box>
          ) : (
            <List dense role="listbox" aria-label="Command palette results">
              {filteredCommands.map((cmd, idx) => (
                <CommandPaletteRow
                  key={cmd.id}
                  cmd={cmd}
                  idx={idx}
                  isSelected={idx === activeIndex}
                  onSelectCommand={handleSelectCommand}
                  setItemRef={setItemRef}
                />
              ))}
            </List>
          )}
        </Box>
      </Dialog>
    </CommandRegistryContext.Provider>
  );
}

export function CommandPaletteProvider({
  children,
}: PropsWithChildren<unknown>) {
  const { isOriginalUserStaff } = useCurrentUser();
  if (!isOriginalUserStaff) return <>{children}</>;
  return <StaffCommandPalette>{children}</StaffCommandPalette>;
}
